import {
  ApolloClient,
  FetchPolicy,
  QueryResult,
  useQuery,
} from '@apollo/client';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { SEARCH_JOBS_LIMIT } from 'src/common/constants';
import { JobsSortByKey } from 'src/common/enums/jobs';
import { tryQuery } from 'src/common/graphql/graphqlHelper';
import { apolloContextWithApiV2AlcClientName } from 'src/modules/ApolloWrapper/constants';
import {
  SEO_PATH_EXPLORE,
  SEO_SOURCE,
  UTM_REFERRER,
  UTM_REFERRER_SOURCE,
} from 'src/modules/Opportunities/constants';

import { searchJobsQueryV3 } from '../graphql';
import { SearchJobsQueryData } from '../graphqlUtils';
import { SearchJobsQueryKey, SearchJobsResponse } from '../types';

const DEFAULT_SORT_BY = JobsSortByKey.LATEST;

interface SearchJobRequestProperties extends SearchJobsQueryData {
  page: number;
  includeExternalJobs: boolean;
  pageSize: number;
  seoSearch: boolean;
}

interface SearchJobsVariables {
  data: SearchJobRequestProperties;
}

export const useGetSearchJobs = (args: {
  jobSearchQuery: SearchJobsQueryData;
  page?: number;
  onCompleted?: (data: SearchJobsResponse) => void;
}): QueryResult<SearchJobsResponse> => {
  const { pathname } = useRouter();
  const sortBy = args.jobSearchQuery[SearchJobsQueryKey.SortBy];
  if (pathname === SEO_PATH_EXPLORE) {
    Cookies.set(SEO_SOURCE, UTM_REFERRER_SOURCE[UTM_REFERRER.SITE]);
  } else {
    Cookies.remove(SEO_SOURCE);
  }

  return useQuery<SearchJobsResponse, SearchJobsVariables>(searchJobsQueryV3, {
    variables: {
      data: {
        ...args.jobSearchQuery,
        page: args.page ?? 1,
        includeExternalJobs: true,
        pageSize: SEARCH_JOBS_LIMIT,
        sortBy: sortBy ?? DEFAULT_SORT_BY,
        seoSearch: true,
      },
    },
    fetchPolicy: 'cache-first' as FetchPolicy,
    context: apolloContextWithApiV2AlcClientName,
    onCompleted: args?.onCompleted,
  });
};

export const tryPreloadGetSearchJobs = async (args: {
  page: number;
  apolloClient: ApolloClient<any>;
  jobSearchQuery: SearchJobsQueryData;
}): Promise<{ response?: SearchJobsResponse; error?: any }> => {
  const sortBy = args.jobSearchQuery[SearchJobsQueryKey.SortBy];
  return tryQuery<SearchJobsResponse>({
    apolloClient: args.apolloClient,
    query: searchJobsQueryV3,
    context: apolloContextWithApiV2AlcClientName,
    variables: {
      data: {
        ...args.jobSearchQuery,
        page: args.page,
        includeExternalJobs: true,
        pageSize: SEARCH_JOBS_LIMIT,
        sortBy: sortBy ?? DEFAULT_SORT_BY,
        seoSearch: true,
      },
    },
  });
};
